import React from 'react';
import { Field, Form } from 'react-final-form';
import { Link } from 'react-router-dom';
import {
    excludeSymbolsRegExp,
    instagramProfileRegExp,
    telegramProfileRegExp,
} from '../../utils/regexps';
import { useTranslation } from '../../utils/useTranslation';
import { composeValidators } from '../../utils/validators/compose';
import { makeMaxLengthValidator } from '../../utils/validators/makeMaxLengthValidator';
import { makeRegExpValidator } from '../../utils/validators/makeRegExpValidator';
import { makeRequiredValidator } from '../../utils/validators/makeRequiredValidator';
import { makeUrlValidator } from '../../utils/validators/makeUrlValidator';
import { WithHint } from '../feedback/Tooltip';
import { Button } from '../input/Button';
import { Checkbox } from '../input/Checkbox';
import { FormControl } from '../input/FormControl';
import { FormControlLabel } from '../input/FormControlLabel';
import { Input } from '../input/Input';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { BusinessRegistrationUI } from './types';

const validateDisplayName = composeValidators(
    makeRequiredValidator<string>(),
    makeMaxLengthValidator(25, 'Название профиля не должно превышать 25 символов'),
    makeRegExpValidator(excludeSymbolsRegExp, 'Не используйте запрещенные символы  ; > < /'),
);

const validateDomain = composeValidators(makeRequiredValidator<string>(), makeUrlValidator());

const validateInstagram = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(
        instagramProfileRegExp,
        'Укажите ссылку в формате https://www.instagram.com/username',
    ),
);

const validateTelegram = composeValidators(
    makeRegExpValidator(telegramProfileRegExp, 'Укажите ссылку в формате https://t.me/username'),
);

const validateOfferAgreement = composeValidators(makeRequiredValidator<string>());

interface BusinessRegistrationFormProps {
    initialValues: Partial<BusinessRegistrationUI>;
    onSubmit: (formValues: BusinessRegistrationUI) => Promise<void>;
    onFieldBlur: (fieldName: string, value: string) => void;
}

export const BusinessRegistrationForm = (props: BusinessRegistrationFormProps) => {
    const { initialValues, onSubmit, onFieldBlur } = props;
    const t = useTranslation();

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            render={({ submitting, pristine, invalid, submitError, handleSubmit }) => (
                <Flex
                    as="form"
                    width="100%"
                    height={{ xs: '100%', sm: 'auto' }}
                    onSubmit={handleSubmit}
                    spacing={1}
                >
                    <Flex
                        px={2}
                        width="100%"
                        height={{ xs: '100%', sm: 'auto' }}
                        borderRadius={2}
                        backgroundColor="var(--background-primary)"
                    >
                        <Typography variant="title2" weight="bold" py={2}>
                            {t('Заполните анкету на участие в бета-тесте Яндекс Ритм')}
                        </Typography>
                        <Typography variant="body">
                            {t(
                                'Данные о бизнесе заполняются один раз, изменить их можно будет только через поддержку.',
                            )}
                        </Typography>
                        <Typography variant="body" pt={1}>
                            {t('Одному логину может принадлежать один бизнес.')}
                        </Typography>

                        <Field
                            name="domain"
                            validate={validateDomain}
                            render={({ input, meta }) => (
                                <FormControl
                                    pt={1}
                                    pb={1}
                                    label={t('Укажите сайт')}
                                    required
                                    error={meta.touched && Boolean(meta.error)}
                                    message={meta.touched ? meta.error : undefined}
                                >
                                    <Input
                                        {...input}
                                        type="text"
                                        autoComplete="off"
                                        placeholder="https://"
                                        onBlur={(event) => {
                                            onFieldBlur('domain', event.target.value);
                                            input.onBlur(event);
                                        }}
                                    />
                                </FormControl>
                            )}
                        />

                        <Typography variant="title3" weight="bold" py={1}>
                            <WithHint
                                message={
                                    'Вводите социальные сети, которые принадлежат вашему бизнесу. Принадлежность аккаунта вам будет проверяться.\n\nПожалуйста, заполните все поля, это увеличивает шанс попасть в бету'
                                }
                            >
                                {t('Социальные сети вашего бизнеса')}
                            </WithHint>
                        </Typography>
                        <Typography variant="body" pt={1}>
                            {t(
                                'Если у вас нет возможности заполнить все обязательные поля, заполните ',
                            )}
                            <Typography
                                as="a"
                                display="inline"
                                href="https://forms.yandex.ru/surveys/13583098.c6d3daeb799a9937ad8e90ed8f79c1ab090651d8/"
                                variant="body"
                                color="link"
                            >
                                {t('форму')}
                            </Typography>
                            <Typography variant="body" pt={1} display="inline">
                                {t(', чтобы следить за их изменениями.')}
                            </Typography>
                        </Typography>
                        <Field
                            name="instagramUrl"
                            validate={validateInstagram}
                            render={({ input, meta }) => (
                                <FormControl
                                    pt={1}
                                    pb={1}
                                    label={t('Инстаграм бизнеса¹')}
                                    required
                                    error={meta.touched && Boolean(meta.error)}
                                    message={meta.touched ? meta.error : undefined}
                                >
                                    <Input
                                        {...input}
                                        type="text"
                                        autoComplete="off"
                                        placeholder="https://www.instagram.com/username"
                                        onBlur={(event) => {
                                            onFieldBlur('instagramUrl', event.target.value);
                                            input.onBlur(event);
                                        }}
                                    />
                                </FormControl>
                            )}
                        />
                        <Field
                            name="telegramUrl"
                            validate={validateTelegram}
                            render={({ input, meta }) => (
                                <FormControl
                                    pt={1}
                                    pb={1}
                                    label={t('Телеграм-канал бизнеса')}
                                    error={meta.touched && Boolean(meta.error)}
                                    message={meta.touched ? meta.error : undefined}
                                >
                                    <Input
                                        {...input}
                                        type="text"
                                        autoComplete="off"
                                        placeholder="https://t.me/username"
                                        onBlur={(event) => {
                                            onFieldBlur('telegramUrl', event.target.value);
                                            input.onBlur(event);
                                        }}
                                    />
                                </FormControl>
                            )}
                        />
                        <Field
                            name="displayName"
                            validate={validateDisplayName}
                            render={({ input, meta }) => (
                                <FormControl
                                    py={1}
                                    required
                                    label={t('Название бизнеса')}
                                    error={meta.touched && Boolean(meta.error)}
                                    message={meta.touched ? meta.error : undefined}
                                >
                                    <Input
                                        {...input}
                                        type="text"
                                        autoComplete="off"
                                        placeholder={t('Не заполнено')}
                                        onBlur={(event) => {
                                            onFieldBlur('displayName', event.target.value);
                                            input.onBlur(event);
                                        }}
                                    />
                                </FormControl>
                            )}
                        />
                        <Field
                            name="email"
                            render={({ input, meta }) => (
                                <FormControl
                                    py={1}
                                    required
                                    label={t('Адрес электронной почты')}
                                    error={meta.touched && Boolean(meta.error)}
                                    message={meta.touched ? meta.error : undefined}
                                    disabled
                                >
                                    <Input
                                        {...input}
                                        type="text"
                                        autoComplete="off"
                                        placeholder={t('Не заполнено')}
                                    />
                                </FormControl>
                            )}
                        />
                        <Typography variant="caption" color="secondary">
                            {t('На вашу почту мы будем присылать важные уведомления от сервиса')}
                        </Typography>
                        <Field
                            type="checkbox"
                            name="offerAgreement"
                            validate={validateOfferAgreement}
                            render={({ input }) => (
                                <FormControlLabel
                                    pt={2}
                                    pb={1}
                                    label={
                                        <Typography>
                                            {t('Я принимаю условия ')}
                                            <Typography
                                                display="inline"
                                                as={Link}
                                                to={process.env.REACT_APP_TERM_OF_USE || ''}
                                                color="link"
                                                textDecoration="none"
                                                target="_blank"
                                            >
                                                {t('пользовательского соглашения')}
                                            </Typography>
                                        </Typography>
                                    }
                                    labelVerticalPadding={0}
                                    control={
                                        <Checkbox
                                            {...input}
                                            onChange={(event) => {
                                                input.onChange(event);
                                                onFieldBlur(
                                                    'offerAgreement',
                                                    event.currentTarget.checked ? 'checked' : '',
                                                );
                                            }}
                                        />
                                    }
                                />
                            )}
                        />
                        <Field
                            type="checkbox"
                            name="sendAdMails"
                            render={({ input }) => (
                                <FormControlLabel
                                    pt={1}
                                    pb={2}
                                    label={t('Я согласен получать письма от Ритма')}
                                    labelVerticalPadding={0}
                                    control={
                                        <Checkbox
                                            {...input}
                                            onChange={(event) => {
                                                input.onChange(event);
                                                onFieldBlur(
                                                    'sendAdMails',
                                                    event.currentTarget.checked ? 'checked' : '',
                                                );
                                            }}
                                        />
                                    }
                                />
                            )}
                        />
                        <Typography variant="caption" color="secondary" pb={2}>
                            {t(
                                '¹Meta, владеющая Instagram, признана экстремистской организацией на территории РФ',
                            )}
                        </Typography>

                        <Flex width="100%" pb={2} pt={1}>
                            <Box mt="auto" width="100%">
                                {submitError && (
                                    <Typography variant="caption" color="error" mb={2}>
                                        {submitError}
                                        <br />
                                    </Typography>
                                )}
                                <Button
                                    width="100%"
                                    type="submit"
                                    disabled={pristine || submitting || invalid}
                                >
                                    {t('Хочу участвовать в бете')}
                                </Button>
                            </Box>
                        </Flex>
                    </Flex>
                </Flex>
            )}
        />
    );
};
